<script setup>
/* global axios */
import { ref, watch, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { messages } from './workflow-ui';
import TeamOwnership from './TeamOwnership.vue';

const props = defineProps({ conversationId: { type: Number, required: true } });
const route = useRoute();
const { t } = useI18n({ messages });
const runs = ref([]);
const error = ref('');
const busy = ref(false);
let epoch = 0;
async function refresh() {
  const version = ++epoch;
  runs.value = [];
  error.value = '';
  busy.value = true;
  try {
    const result = await axios.get(
      `/api/v1/accounts/${route.params.accountId}/chat_flows/conversation/${props.conversationId}/runs`
    );
    if (version === epoch) runs.value = result.data;
  } catch (e) {
    if (version === epoch) error.value = e.response?.data?.error || e.message;
  } finally {
    if (version === epoch) busy.value = false;
  }
}
async function stop(run) {
  busy.value = true;
  const version = epoch;
  try {
    await axios.post(
      `/api/v1/accounts/${route.params.accountId}/chat_flows/runs/${run.id}/stop`
    );
    if (version === epoch) await refresh();
  } catch (e) {
    if (version === epoch) error.value = e.response?.data?.error || e.message;
  } finally {
    if (version === epoch) busy.value = false;
  }
}
watch(() => [route.params.accountId, props.conversationId], refresh, {
  immediate: true,
});
onBeforeUnmount(() => {
  epoch += 1;
});
</script>

<template>
  <TeamOwnership :conversation-id="conversationId" />
  <section
    class="max-h-52 shrink-0 overflow-auto border-b border-n-weak px-4 py-3"
  >
    <div class="flex items-center justify-between">
      <h3 class="text-sm font-semibold">{{ t('WF.title') }}</h3>
      <button
        class="text-xs text-n-blue-10 disabled:opacity-40"
        :disabled="busy"
        @click="refresh"
      >
        {{ t('WF.refresh') }}
      </button>
    </div>
    <p v-if="error" class="mt-2 text-xs text-red-700">{{ error }}</p>
    <p v-else-if="!runs.length" class="mt-2 text-xs text-n-slate-10">
      {{ t('WF.emptyRuns') }}
    </p>
    <article v-for="run in runs.slice(0, 3)" :key="run.id" class="mt-3 text-xs">
      <p>#{{ run.flow_id }} · {{ t(`WF.${run.status}`) }}</p>
      <p class="mt-1 text-n-slate-10">{{ run.state.log.at(-1)?.detail }}</p>
      <button
        v-if="['running', 'waiting', 'external'].includes(run.status)"
        class="mt-2 rounded border border-n-weak px-2 py-1 disabled:opacity-40"
        :disabled="busy"
        @click="stop(run)"
      >
        {{ t('WF.stopRun') }}
      </button>
    </article>
  </section>
</template>
